/* =====================================================================
   ccg-multilikes.js
   =====================================================================
   MultiLikes App Proxy 経由のお気に入りボタン Alpine.js コンポーネント。

   App Proxy URL: https://carcityguide.com/apps/multilikes-endpoint
   (App は carcityguide.com 側に install 済。dev store からも cross-origin で fetch)

   対応 target_type:
     - article
     - product (variant_id 必須)
     - collection
     - page
     - metaobject (metaobject_type 必須)

   使い方: snippet `ccg-multilikes-button.liquid` 経由で x-data 設定。
   未ログインは customer_id 空 → toggle 時に /pages/signin にリダイレクト。
   ===================================================================== */
(function () {
  const BASE = 'https://carcityguide.com/apps/multilikes-endpoint';

  function buildParams(config, customerId, extra) {
    const p = new URLSearchParams();
    if (config.targetType === 'metaobject') {
      p.set('metaobject_type', config.metaobjectType);
    } else {
      p.set('target_type', config.targetType);
    }
    if (customerId) p.set('customer_id', customerId);

    switch (config.targetType) {
      case 'article':
        p.set('article_id', config.resourceId);
        break;
      case 'product':
        p.set('product_id', config.resourceId);
        if (config.variantId) p.set('variant_id', config.variantId);
        break;
      case 'collection':
        p.set('collection_id', config.resourceId);
        break;
      case 'page':
        p.set('page_id', config.resourceId);
        break;
      case 'metaobject':
        if (extra && extra.includeId) {
          // POST 時のみ
          p.set('metaobject_id', config.resourceId);
        }
        break;
    }
    if (extra && extra.action) p.set('action', extra.action);
    return p;
  }

  function multilikesButton(config) {
    return {
      // state
      isInWishlist: false,
      favoriteCount: 0,
      loading: false,
      customerId: (window.CCG_CUSTOMER_ID || '').toString(),

      // config はクロージャ参照
      _config: config,

      async init() {
        if (!this.customerId) {
          // 未ログインでも count は取れるが、初期 GET をスキップして default 状態のまま (パフォーマンス優先)
          return;
        }
        await this.fetchState();
      },

      async fetchState() {
        try {
          const params = buildParams(this._config, this.customerId);
          const res = await fetch(BASE + '?' + params.toString(), {
            credentials: 'same-origin',
          });
          const json = await res.json();
          if (json && json.data) {
            this.isInWishlist = !!json.data.isInWishlist;
            this.favoriteCount = json.data.favoriteCount != null ? json.data.favoriteCount : this.favoriteCount;
          }
        } catch (e) {
          if (window.console) console.warn('[multilikes] fetchState failed', e);
        }
      },

      async toggle() {
        if (!this.customerId) {
          // 未ログイン → ログイン誘導モーダルを起動 (ccg-login-modal が listener)
          window.dispatchEvent(new CustomEvent('ccg-login-required', {
            detail: { returnUrl: window.location.pathname + window.location.search }
          }));
          return;
        }
        if (this.loading) return;
        this.loading = true;
        const action = this.isInWishlist ? 'remove' : 'add';
        const body = buildParams(this._config, this.customerId, {
          action: action,
          includeId: this._config.targetType === 'metaobject',
        });
        // POST 用: target_type=metaobject の場合は target_type を明示的に追加 (buildParams は GET 用に metaobject_type を入れている)
        if (this._config.targetType === 'metaobject') {
          body.set('target_type', 'metaobject');
        }
        try {
          const res = await fetch(BASE, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
          });
          const json = await res.json();
          if (json && json.data && json.data.success) {
            this.isInWishlist = !!json.data.isInWishlist;
            if (json.data.favoriteCount != null) this.favoriteCount = json.data.favoriteCount;
          } else if (json && json.error) {
            if (window.console) console.warn('[multilikes] toggle error:', json.error);
          }
        } catch (e) {
          if (window.console) console.warn('[multilikes] toggle failed', e);
        } finally {
          this.loading = false;
        }
      },
    };
  }

  // ----- 顧客のお気に入り一覧 (account ページ用) -----
  // 使用: <div x-data="customerFavorites()" x-init="init()"> ... <template x-for=...
  function customerFavorites() {
    return {
      articles: [],
      products: [],
      loadingArticles: true,
      loadingProducts: true,
      customerId: (window.CCG_CUSTOMER_ID || '').toString(),

      async init() {
        if (!this.customerId) {
          this.loadingArticles = false;
          this.loadingProducts = false;
          return;
        }
        await Promise.all([this.fetchArticles(), this.fetchProducts()]);
      },

      async fetchArticles() {
        try {
          const url = BASE + '?target_type=article&mode=customer_wishlist'
            + '&customer_id=' + encodeURIComponent(this.customerId) + '&limit=8';
          const res = await fetch(url, { credentials: 'same-origin' });
          const json = await res.json();
          this.articles = (json && Array.isArray(json.data)) ? json.data : [];
        } catch (e) {
          if (window.console) console.warn('[multilikes] favorites/articles failed', e);
        } finally {
          this.loadingArticles = false;
        }
      },

      async fetchProducts() {
        try {
          const url = BASE + '?target_type=product&mode=customer_wishlist'
            + '&customer_id=' + encodeURIComponent(this.customerId) + '&limit=8';
          const res = await fetch(url, { credentials: 'same-origin' });
          const json = await res.json();
          this.products = (json && Array.isArray(json.data)) ? json.data : [];
        } catch (e) {
          if (window.console) console.warn('[multilikes] favorites/products failed', e);
        } finally {
          this.loadingProducts = false;
        }
      },

      formatDate(iso) {
        if (!iso) return '';
        try {
          const d = new Date(iso);
          return d.getFullYear() + '.' + (d.getMonth() + 1) + '.' + d.getDate();
        } catch (e) { return ''; }
      },
    };
  }

  // window グローバルに公開 (Alpine の x-data="multilikesButton({...})" から直接呼べるように)
  window.multilikesButton = multilikesButton;
  window.customerFavorites = customerFavorites;
})();
